import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { CSVLink } from 'react-csv';

const HaqdarshakLeaderboard = () => {
  const [allTopHds, setAllTopHds] = useState([]);
  const [allBottomHds, setAllBottomHds] = useState([]);
  const [loading, setLoading] = useState({ top: false, bottom: false });
  const [error, setError] = useState({ top: null, bottom: null });
  const pid = useSelector((state) => state.user.projectId);

  const limitOptions = [5, 10, 20, 50, 100, 1000, 10000];
  const [topLimit, setTopLimit] = useState(10);
  const [bottomLimit, setBottomLimit] = useState(10);

  const [orgOptions, setOrgOptions] = useState([]);
  const [schemeTypeOptions, setSchemeTypeOptions] = useState([]);
  
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [selectedSchemeTypes, setSelectedSchemeTypes] = useState([]);
  const [filterTimeout, setFilterTimeout] = useState(null);

  // State for info popups
  const [infoPopup, setInfoPopup] = useState({ show: false, title: '', description: '', example: '' });

  // Definitions for info popups
  const definitions = {
    "HD Performance Leaderboard Based On BR Count": {
      title: "HD Performance Leaderboard Based On BR Count",
      description: "This leaderboard shows the performance of Haqdarshaks (HDs) based on the number of Benefit Received (BR) cases they have completed. Top performers are ranked highest while bottom performers have the lowest BR counts.",
      example: "• Top performers: HDs with highest BR counts\n• Bottom performers: HDs with lowest BR counts\n• BR Count: Number of applications with Benefit Received status"
    },
    "Organization": {
      title: "Organization",
      description: "Filter the leaderboard by specific organizations to compare HD performance within or across organizations.",
      example: "Example: Maharashtra, Karnataka, Delhi NCR, etc."
    },
    "Service Type": {
      title: "Service Type",
      description: "Filter by service type to analyze HD performance for specific service categories.",
      example: "Example: Schemes, Document, DFL, Yojana Card"
    },
    "Top Performers": {
      title: "Top Performers",
      description: "Haqdarshaks with the highest number of Benefit Received (BR) cases. These are the top-performing field workers.",
      example: "• Rank 1: Highest BR count\n• Rank 2: Second highest BR count\n• Rank 3: Third highest BR count"
    },
    "Bottom Performers": {
      title: "Bottom Performers",
      description: "Haqdarshaks with the lowest number of Benefit Received (BR) cases. These may need additional support or training.",
      example: "• Bottom performers show lower BR counts\n• May indicate areas for improvement\n• Could be newer HDs or in challenging areas"
    },
    "Rank": {
      title: "Rank",
      description: "The position of the Haqdarshak based on their BR count. Rank 1 is the highest performer.",
      example: "• Gold: Rank 1 (Highest)\n• Silver: Rank 2\n• Bronze: Rank 3"
    },
    "HD Name": {
      title: "HD Name",
      description: "The name of the Haqdarshak (field worker) who is responsible for helping citizens with their applications.",
      example: "Example: Rajesh Kumar, Priya Sharma, etc."
    },
    "BR Count": {
      title: "BR Count",
      description: "The total number of Benefit Received (BR) cases completed by the Haqdarshak. This represents applications where citizens have successfully received benefits.",
      example: "• Higher BR count = Better performance\n• BR = Benefit Received status"
    }
  };

  // Info Button Component
  const InfoButton = ({ title }) => {
    const definition = definitions[title];
    if (!definition) return null;

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setInfoPopup({
            show: true,
            title: definition.title,
            description: definition.description,
            example: definition.example || ''
          });
        }}
        className="group relative ml-1.5 inline-flex items-center justify-center w-4 h-4 flex-shrink-0 rounded-full text-gray-400 transition-colors duration-300 ease-out hover:text-indigo-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-indigo-400"
        aria-label={`Info about ${title}`}
      >
        <span className="absolute inset-0 -m-2 rounded-full bg-white/0 ring-1 ring-transparent transition-all duration-300 ease-out group-hover:bg-white/70 group-hover:backdrop-blur-md group-hover:ring-black/5 group-hover:shadow-[0_4px_14px_rgba(99,102,241,0.18)]" />
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="relative w-3.5 h-3.5 transition-transform duration-300 ease-out group-hover:scale-110"
        >
          <circle cx="12" cy="12" r="9.5" />
          <path d="M12 16v-4.5" />
          <path d="M12 7.8h.01" />
        </svg>
      </button>
    );
  };

  // Info Popup Component
  const InfoPopup = () => {
    if (!infoPopup.show) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]" onClick={closeInfoPopup}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-0 overflow-hidden animate-scaleIn" onClick={(e) => e.stopPropagation()}>
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 18a9 9 0 100-18 9 9 0 000 18z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white">{infoPopup.title}</h3>
            </div>
            <button
              onClick={closeInfoPopup}
              className="text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="px-6 py-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="mt-0.5">
                <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-700 leading-relaxed">{infoPopup.description}</p>
            </div>

            {infoPopup.example && (
              <div className="mt-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">📊</span>
                  <p className="text-sm font-semibold text-gray-800">Example</p>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-line font-mono bg-white/50 p-3 rounded-lg border border-blue-100">
                  {infoPopup.example}
                </p>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closeInfoPopup}
                className="px-5 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg text-sm font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Close info popup
  const closeInfoPopup = () => {
    setInfoPopup({ show: false, title: '', description: '', example: '' });
  };

  // Derived data - slice based on limits
  const displayedTopHds = allTopHds.slice(0, topLimit);
  const displayedBottomHds = allBottomHds.slice(0, bottomLimit);
  const totalTopHds = allTopHds.length;
  const totalBottomHds = allBottomHds.length;

  const handleFilterChange = () => {
    if (filterTimeout) clearTimeout(filterTimeout);
    setFilterTimeout(setTimeout(() => {
      fetchTopHds();
      fetchBottomHds();
    }, 500));
  };

  const getQueryParams = () => {
    const params = new URLSearchParams();
    params.append('pid', pid);
    selectedOrgs.forEach(org => params.append('OrgName', org.value));
    selectedSchemeTypes.forEach(type => params.append('SchemeType', type.value));
    return params;
  };

  const fetchOrgOptions = async () => {
    try {
      const response = await fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-org/?pid=${pid}`);
      const data = await response.json();
      setOrgOptions(data.unique_Organizations.map(org => ({ value: org, label: org })));
    } catch (error) {
      console.error('Error fetching org options:', error);
    }
  };

  const fetchSchemeTypeOptions = async () => {
    try {
      const response = await fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-scheme-type/?pid=${pid}`);
      const data = await response.json();
      
      const schemeTypeMap = {
        'sch': 'Schemes',
        'doc': 'Document',
        'dfl': 'DFL',
        'yc': 'Yojana Card'
      };
      
      setSchemeTypeOptions(data.unique_Scheme_Types.map(type => ({
        value: type,
        label: schemeTypeMap[type] || type
      })));
    } catch (error) {
      console.error('Error fetching scheme type options:', error);
    }
  };

  const fetchTopHds = async () => {
    setLoading(prev => ({ ...prev, top: true }));
    setError(prev => ({ ...prev, top: null }));
    try {
      const response = await fetch(
        `https://api-project360-insights.haqdarshak.com/kpi/comparison/top-hds/?${getQueryParams()}`
      );
      if (!response.ok) throw new Error('Failed to fetch top HDs');
      const data = await response.json();
      setAllTopHds(data.top_hds || []);
    } catch (err) {
      setError(prev => ({ ...prev, top: err.message }));
      console.error('Error fetching top HDs:', err);
    } finally {
      setLoading(prev => ({ ...prev, top: false }));
    }
  };

  const fetchBottomHds = async () => {
    setLoading(prev => ({ ...prev, bottom: true }));
    setError(prev => ({ ...prev, bottom: null }));
    try {
      const response = await fetch(
        `https://api-project360-insights.haqdarshak.com/kpi/comparison/bottom-hds/?${getQueryParams()}`
      );
      if (!response.ok) throw new Error('Failed to fetch bottom HDs');
      const data = await response.json();
      setAllBottomHds(data.bottom_hds || []);
    } catch (err) {
      setError(prev => ({ ...prev, bottom: err.message }));
      console.error('Error fetching bottom HDs:', err);
    } finally {
      setLoading(prev => ({ ...prev, bottom: false }));
    }
  };

  useEffect(() => {
    fetchOrgOptions();
    fetchSchemeTypeOptions();
    fetchTopHds();
    fetchBottomHds();
  }, [pid]);

  useEffect(() => {
    handleFilterChange();
    return () => {
      if (filterTimeout) clearTimeout(filterTimeout);
    };
  }, [selectedOrgs, selectedSchemeTypes]);

  const clearFilters = () => {
    setSelectedOrgs([]);
    setSelectedSchemeTypes([]);
  };

  const getRankColor = (rank) => {
    if (rank === 1) return 'bg-yellow-500 text-white';
    if (rank === 2) return 'bg-gray-400 text-white';
    if (rank === 3) return 'bg-orange-600 text-white';
    return 'bg-gray-100 text-gray-600';
  };

  // Prepare CSV data - Combine ALL top and bottom HDs together
  const prepareCSV = () => {
    const headers = ['Rank', 'HD Name', 'HDID', 'BR Count'];
    const topRows = allTopHds.map(hd => [
      hd.rank,
      hd.haqdarshakName || hd.hd_name || 'N/A',
      hd.HDID || 'N/A',
      hd.br_count || 0
    ]);
    
    return [headers, ...topRows];
  };

  const getCSVFilename = () => {
    const date = new Date().toISOString().split('T')[0];
    return `haqdarshak-leaderboard-br-${date}.csv`;
  };

  return (
    <div className="w-[90vw]">
      <div className="border-2 border-gray-300 shadow-md rounded-md overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-4 md:px-6 py-4 border-b">
          <div className="flex items-center">
            <h2 className="text-lg md:text-xl font-semibold text-pink-800">
              HD Performance Leaderboard Based On BR Count
            </h2>
            <InfoButton title="HD Performance Leaderboard Based On BR Count" />
          </div>
          <p className="text-sm text-pink-800 mt-1">
            Compare top and bottom performing Haqdarshaks based on BR cases
          </p>
        </div>

        {/* Filter Section */}
        <div className="bg-white p-4 md:p-6 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <div className="flex items-center mb-2">
                <label className="text-sm font-medium text-gray-700">Organization</label>
                <InfoButton title="Organization" />
              </div>
              <Select
                isMulti
                options={orgOptions}
                value={selectedOrgs}
                onChange={(selected) => setSelectedOrgs(selected || [])}
                className="basic-multi-select"
                classNamePrefix="select"
                placeholder="Select organizations..."
                closeMenuOnSelect={false}
              />
            </div>
            
            <div>
              <div className="flex items-center mb-2">
                <label className="text-sm font-medium text-gray-700">Service Type</label>
                <InfoButton title="Service Type" />
              </div>
              <Select
                isMulti
                options={schemeTypeOptions}
                value={selectedSchemeTypes}
                onChange={(selected) => setSelectedSchemeTypes(selected || [])}
                className="basic-multi-select"
                classNamePrefix="select"
                placeholder="Select service types..."
                closeMenuOnSelect={false}
              />
            </div>

            {/* Top Limit Selection */}
            <div>
              <div className="flex items-center mb-2">
                <label className="text-sm font-medium text-gray-700">Top Performers</label>
                <InfoButton title="Top Performers" />
              </div>
              <select
                value={topLimit}
                onChange={(e) => setTopLimit(Number(e.target.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent transition"
              >
                {limitOptions.map(limit => (
                  <option key={`top-${limit}`} value={limit}>{limit}</option>
                ))}
              </select>
            </div>

            {/* Bottom Limit Selection */}
            <div>
              <div className="flex items-center mb-2">
                <label className="text-sm font-medium text-gray-700">Bottom Performers</label>
                <InfoButton title="Bottom Performers" />
              </div>
              <select
                value={bottomLimit}
                onChange={(e) => setBottomLimit(Number(e.target.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent transition"
              >
                {limitOptions.map(limit => (
                  <option key={`bottom-${limit}`} value={limit}>{limit}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-3 flex justify-between items-center flex-wrap gap-2">
            <div className="text-sm text-gray-500">
              Total unique HDs: <span className="font-semibold">{totalTopHds}</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={clearFilters}
                className="bg-gray-200 hover:bg-gray-300 transition px-4 py-2 rounded-lg text-sm"
              >
                Clear Filters
              </button>
              <CSVLink
                data={prepareCSV()}
                filename={getCSVFilename()}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm transition flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export CSV
              </CSVLink>
            </div>
          </div>
        </div>

        {/* Leaderboard Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-4 md:p-6 bg-gray-50">
          
          {/* Top Performers Card */}
          <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-600 to-gray-700 px-4 py-3">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                <h3 className="text-white font-semibold">Top {topLimit} Performers</h3>
                <InfoButton title="Top Performers" />
              </div>
            </div>
            
            <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
              {loading.top ? (
                <div className="p-8 flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-500"></div>
                </div>
              ) : error.top ? (
                <div className="p-4 text-red-500 text-center">{error.top}</div>
              ) : displayedTopHds.length > 0 ? (
                displayedTopHds.map((hd) => (
                  <div key={hd.HDID} className="px-4 py-3 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center space-x-4">
                      <span className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full ${getRankColor(hd.rank)} font-medium text-sm`}>
                        {hd.rank}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {hd.haqdarshakName || hd.hd_name}
                        </p>
                        <p className="text-xs text-gray-500">HDID: {hd.HDID}</p>
                      </div>
                      <div className="inline-flex items-center text-base font-semibold text-green-600">
                        {hd.br_count?.toLocaleString() || 0}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-gray-400">No data available</div>
              )}
            </div>
            {totalTopHds > topLimit && (
              <div className="px-4 py-2 bg-gray-50 text-center text-xs text-gray-500 border-t">
                Showing {Math.min(topLimit, totalTopHds)} of {totalTopHds} total HDs
              </div>
            )}
          </div>

          {/* Bottom Performers Card */}
          <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-600 to-gray-700 px-4 py-3">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                </svg>
                <h3 className="text-white font-semibold">Bottom {bottomLimit} Performers</h3>
                <InfoButton title="Bottom Performers" />
              </div>
            </div>
            
            <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
              {loading.bottom ? (
                <div className="p-8 flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-500"></div>
                </div>
              ) : error.bottom ? (
                <div className="p-4 text-red-500 text-center">{error.bottom}</div>
              ) : displayedBottomHds.length > 0 ? (
                displayedBottomHds.map((hd) => (
                  <div key={hd.HDID} className="px-4 py-3 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center space-x-4">
                      <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 text-gray-700 font-medium text-sm">
                        {hd.rank}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {hd.haqdarshakName || hd.hd_name}
                        </p>
                        <p className="text-xs text-gray-500">HDID: {hd.HDID}</p>
                      </div>
                      <div className="inline-flex items-center text-base font-semibold text-red-600">
                        {hd.br_count?.toLocaleString() || 0}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-gray-400">No data available</div>
              )}
            </div>
            {totalBottomHds > bottomLimit && (
              <div className="px-4 py-2 bg-gray-50 text-center text-xs text-gray-500 border-t">
                Showing {Math.min(bottomLimit, totalBottomHds)} of {totalBottomHds} total HDs
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Info Popup */}
      <InfoPopup />

      <style jsx>{`
        @keyframes scaleIn {
          from { 
            opacity: 0; 
            transform: scale(0.95) translateY(-10px); 
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }
        .animate-scaleIn {
          animation: scaleIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </div>
  );
};

export default HaqdarshakLeaderboard;